// Constants
import { DueDateCategory } from "@ms/uno-constants/lib/local/DateConstants";
// Models
import { ProgressState } from "@ms/uno-models/lib/local/client/legacyPlanner/progressState/ProgressState";
import { PriorityName } from "@ms/uno-models/lib/local/client/legacyPlanner/task/ITask";
import { ContextDisplayType } from "@ms/uno-models/lib/local/client/legacyPlanner/plan/ContextDisplayType";
import { SupportedReferenceTypes } from "@ms/uno-models/lib/local/client/taskAttachment/ITaskAttachmentType";
import { ContainerType } from "@ms/uno-models/lib/local/service/graph-legacy/container/ContainerType";
import { UnoTheme } from "@ms/uno-models/lib/local/theme/UnoTheme";
// Resources
import loopSvg from "@ms/uno-resources/lib/local/images/logos/loop.svg";
import excelSvg from "@ms/uno-resources/lib/local/images/logos/excel.svg";
import wordSvg from "@ms/uno-resources/lib/local/images/logos/word.svg";
import teamsSvg from "@ms/uno-resources/lib/local/images/logos/teams.svg";
import sharepointSvg from "@ms/uno-resources/lib/local/images/logos/sharepoint.svg";
import powerpointSvg from "@ms/uno-resources/lib/local/images/logos/powerpoint.svg";
import pageLinkSvg from "@ms/uno-resources/lib/local/images/logos/pagelink.svg";
import pdfSvg from "@ms/uno-resources/lib/local/images/logos/pdf.svg";
// Telemetry
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
// Utilities
import { concatStyleSets } from "@fluentui/react";
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
import { getPortfolioItemStatusColor, getPriorityColor, getProgressColor } from "../utilities/ColorUtilities";
import { calendarDueDateLateProps, calendarDueDateNoDateProps, calendarDueDateTodayProps, calendarDueDateTomorrowProps, calendarDueDateWeekProps, calendarIconProps, homeIconProps, outlookLogoIconProps, portfolioStatusProps, priorityImportantIconProps, priorityImportantSmallIconProps, priorityLowIconProps, priorityLowSmallIconProps, priorityMediumIconProps, priorityUrgentIconProps, priorityUrgentSmallIconProps, progressCompleteIconProps, progressCompleteSmallIconProps, progressInProgressIconProps, progressInProgressSmallIconProps, progressNotStartedIconProps, progressNotStartedSmallIconProps, taskListFilledIconProps, taskListIconProps, sharepointRegularIconProps, sharepointRegularDarkIconProps, sharepointRegularHighContrastIconProps } from "./icons.styles";
// MS branded logo icons
import { ExcelLogo } from "./svgs/ExcelLogo";
import { PowerPointLogo } from "./svgs/PowerPointLogo";
import { WordLogo } from "./svgs/WordLogo";
export var IconSize;
(function(IconSize) {
    IconSize[IconSize["Large"] = 0] = "Large";
    IconSize[IconSize["Medium"] = 1] = "Medium";
    IconSize[IconSize["Small"] = 2] = "Small";
})(IconSize || (IconSize = {}));
/**
 * Get icon props for progress from given progress state and icon size
 * @param theme Planner theme
 * @param progress Progress to get icon props for
 * @param size Size of the icon
 * @param traceLogger Trace logger
 */ export function getProgressIconProps(theme, progress, traceLogger, size = 1) {
    let mediumIconProps = {};
    let smallIconProps = {};
    // Not all icon/size combinations are currently being used.
    // Each icon size requires an extra icon import, so don't import the ones not being used.
    switch(progress){
        case ProgressState.NotStarted:
            mediumIconProps = progressNotStartedIconProps;
            smallIconProps = progressNotStartedSmallIconProps;
            break;
        case ProgressState.InProgress:
            mediumIconProps = progressInProgressIconProps;
            smallIconProps = progressInProgressSmallIconProps;
            break;
        case ProgressState.Complete:
            mediumIconProps = progressCompleteIconProps;
            smallIconProps = progressCompleteSmallIconProps;
            break;
        default:
            ErrorUtilities.unreachableCase(progress, (log)=>{
                traceLogger.logTrace(0x1e483604 /* tag_4sdye */ , TraceLevel.Error, log);
            });
            break;
    }
    let iconProps = {};
    switch(size){
        case 1:
            iconProps = mediumIconProps;
            break;
        case 2:
            iconProps = smallIconProps;
            break;
        default:
            iconProps = mediumIconProps;
    }
    const iconStyles = {
        root: {
            color: getProgressColor(theme, progress)
        }
    };
    return {
        ...iconProps,
        styles: concatStyleSets(iconProps.styles, iconStyles)
    };
}
/**
 * Get icon props for priority from given priority
 * @param theme Planner theme
 * @param priority Priority to get icon props for
 * @param size Size of the icon
 * @param traceLogger Trace logger
 */ export function getPriorityIconProps(theme, priority, traceLogger, size = 1) {
    let mediumIconProps = {};
    let smallIconProps = {};
    switch(priority){
        case PriorityName.Urgent:
            mediumIconProps = priorityUrgentIconProps;
            smallIconProps = priorityUrgentSmallIconProps;
            break;
        case PriorityName.Important:
            mediumIconProps = priorityImportantIconProps;
            smallIconProps = priorityImportantSmallIconProps;
            break;
        case PriorityName.Medium:
            mediumIconProps = priorityMediumIconProps;
            smallIconProps = priorityMediumIconProps;
            break;
        case PriorityName.Low:
            mediumIconProps = priorityLowIconProps;
            smallIconProps = priorityLowSmallIconProps;
            break;
        default:
            ErrorUtilities.unreachableCase(priority, (log)=>{
                traceLogger.logTrace(0x1e483603 /* tag_4sdyd */ , TraceLevel.Error, log);
            });
            break;
    }
    let iconProps = {};
    switch(size){
        case 1:
            iconProps = mediumIconProps;
            break;
        case 2:
            iconProps = smallIconProps;
            break;
        default:
            iconProps = mediumIconProps;
    }
    const iconStyles = {
        root: {
            color: getPriorityColor(theme, priority)
        }
    };
    return {
        ...iconProps,
        styles: concatStyleSets(iconProps.styles, iconStyles)
    };
}
/**
 * Get icon props for portfolio status from given status
 * @param theme Planner theme
 * @param status Status to get icon props for
 */ export function getPortfolioStatusIconProps(theme, status) {
    const iconProps = portfolioStatusProps;
    const iconStyles = {
        root: {
            color: getPortfolioItemStatusColor(theme, status)
        }
    };
    return {
        ...iconProps,
        styles: concatStyleSets(iconProps.styles, iconStyles)
    };
}
/**
 * Get icon props for priority from given due date
 * @param priority Priority to get icon props for
 * @param size Size of the icon
 * @param traceLogger Trace logger
 */ export function getDueDateIconProps(dueDate, traceLogger) {
    let iconProps = {};
    switch(dueDate){
        case DueDateCategory.Late:
            iconProps = calendarDueDateLateProps;
            break;
        case DueDateCategory.Today:
            iconProps = calendarDueDateTodayProps;
            break;
        case DueDateCategory.Tomorrow:
            iconProps = calendarDueDateTomorrowProps;
            break;
        case DueDateCategory.ThisWeek:
        case DueDateCategory.NextWeek:
            iconProps = calendarDueDateWeekProps;
            break;
        case DueDateCategory.Future:
            iconProps = calendarIconProps;
            break;
        case DueDateCategory.NoDate:
            iconProps = calendarDueDateNoDateProps;
            break;
        default:
            ErrorUtilities.unreachableCase(dueDate, (log)=>{
                traceLogger.logTrace(0x1e483602 /* tag_4sdyc */ , TraceLevel.Error, log);
            });
            break;
    }
    return {
        ...iconProps,
        className: iconProps.className
    };
}
/**
 * Gets the associated css class for a given type
 * @param fileType The file type
 */ export function getFallBackPreviewIconColor(fileType) {
    switch(fileType){
        case SupportedReferenceTypes.Excel:
            return "excelIconColor";
        case SupportedReferenceTypes.PowerPoint:
            return "powerPointIconColor";
        case SupportedReferenceTypes.Word:
            return "wordIconColor";
        case SupportedReferenceTypes.OneNote:
            return "oneNoteIconColor";
        case SupportedReferenceTypes.Pdf:
            return "pdfIconColor";
        case SupportedReferenceTypes.Project:
            return "projectIconColor";
        case SupportedReferenceTypes.Visio:
            return "visioIconColor";
        default:
            return "genericIconColor";
    }
}
/**
 * Gets the associated icon for a given type
 * @param fileType The file type
 * @param [defaultIcon] The default icon to use if it doesn't match an explicitly supported file type
 */ export function getTypeIconClass(fileType, defaultIcon) {
    switch(fileType){
        case SupportedReferenceTypes.Excel:
            return "plannerExcelLogo";
        case SupportedReferenceTypes.OneNote:
            return "plannerOneNoteLogo";
        case SupportedReferenceTypes.Pdf:
            return "plannerPdf";
        case SupportedReferenceTypes.PowerPoint:
            return "plannerPowerPointLogo";
        case SupportedReferenceTypes.Project:
            return "plannerProjectLogo";
        case SupportedReferenceTypes.Visio:
            return "plannerVisioLogo";
        case SupportedReferenceTypes.Word:
            return "plannerWordLogo";
        default:
            return defaultIcon ?? "plannerLink";
    }
}
/**
 * Get document plan icon image
 * @param docType Document type of a document plan
 */ export function getDocPlanImage(docType) {
    let image;
    if (docType === SupportedReferenceTypes.Word) {
        image = WordLogo;
    }
    if (docType === SupportedReferenceTypes.Excel) {
        image = ExcelLogo;
    }
    if (docType === SupportedReferenceTypes.PowerPoint) {
        image = PowerPointLogo;
    }
    return image;
}
/**
 * Gets the SVG icon for a filetype
 * @param fileType The file type
 */ export function getSvgIconByFileType(fileType) {
    // This function is only used by the Shared Container, where we are expecting only Fluid files for now.
    switch(fileType){
        case SupportedReferenceTypes.Loop:
            return loopSvg;
        case SupportedReferenceTypes.Excel:
            return excelSvg;
        case SupportedReferenceTypes.Word:
            return wordSvg;
        case SupportedReferenceTypes.PowerPoint:
            return powerpointSvg;
        case SupportedReferenceTypes.Pdf:
            return pdfSvg;
        default:
            return pageLinkSvg;
    }
}
/**
 * Get the context icon image based on context display type
 * @param displayAs Context display type used to get icon image
 * @param traceLogger Trace logger
 */ export function getContextItemImage(displayAs, traceLogger) {
    let image = "";
    switch(displayAs){
        case ContextDisplayType.MeetingNotes:
        case ContextDisplayType.LoopPage:
            image = loopSvg;
            break;
        case ContextDisplayType.SharePointPage:
            image = sharepointSvg;
            break;
        case ContextDisplayType.TeamsTab:
            image = teamsSvg;
            break;
        case ContextDisplayType.Other:
            image = pageLinkSvg;
            break;
        default:
            ErrorUtilities.unreachableCase(displayAs, (log)=>{
                traceLogger.logTrace(0x1e31f498 /* tag_4m5sy */ , TraceLevel.Error, log);
            });
            break;
    }
    return image;
}
/**
 * Get the icon props for the sharepoint icon
 * We have different icons for dark and light themes
 * @param theme Theme to get icon props for
 */ export function getSharepointIconProps(theme) {
    switch(theme.name){
        case UnoTheme.TeamsDark:
        case UnoTheme.Dark:
        case UnoTheme.LoopDark:
        case UnoTheme.LoopWorkspaceDark:
            return sharepointRegularDarkIconProps;
        case UnoTheme.TeamsLight:
        case UnoTheme.Light:
        case UnoTheme.LoopLight:
        case UnoTheme.LoopWorkspaceLight:
            return sharepointRegularIconProps;
        case UnoTheme.TeamsContrast:
            return sharepointRegularHighContrastIconProps;
        default:
            return sharepointRegularIconProps;
    }
}
/**
 * Get the plan container icon (outlook, todo, group photo, initials)
 * @param containerId Container id for the plan
 * @param containerType Container type for the plan
 * @param flaggedEmailPlanId Flagged email plan id
 * @param defaultPlanId Default plan id
 * @param getGroupPhotoById Callback to get group photo id
 */ export function getPlanContainerIcon(containerId, containerType, flaggedEmailPlanId, defaultPlanId, getGroupPhotoById) {
    if (containerId === defaultPlanId) {
        return {
            normalIcon: homeIconProps
        };
    }
    if (containerId === flaggedEmailPlanId) {
        return {
            normalIcon: outlookLogoIconProps
        };
    }
    if (containerType === ContainerType.ToDo) {
        return {
            normalIcon: taskListIconProps,
            focusedIcon: taskListFilledIconProps
        };
    }
    if (containerType === ContainerType.Group) {
        return getGroupPhotoById(containerId);
    }
    return null;
}
